import { CheckboxCard } from "@/components/ui/checkbox-card.tsx";
import {
  ACCESS_CHOICE_INTRO,
  ACCESS_CHOICE_TITLE,
  type AccessItem,
} from "@/shared/accessSelection.ts";
import { CalendarDays, Mail, Users } from "lucide-react";
import type { ReactNode } from "react";

/**
 * The web rendering of "Choose what Okthx can use": the sign-up access page
 * and the connector prompt both draw it from here. Its copy, list and
 * selection are shared with iOS (`shared/accessSelection.ts`,
 * `shared/useAccessChoice.ts`).
 */

/**
 * The icon for each access id. Separate from the copy, which the iOS app shows
 * too — icons are the one part that cannot be shared, since the two platforms
 * draw from different packages.
 */
const ACCESS_ICONS: Record<string, ReactNode> = {
  email: <Mail className="size-5 text-zinc-500" aria-hidden />,
  calendar: <CalendarDays className="size-5 text-zinc-500" aria-hidden />,
  contacts: <Users className="size-5 text-zinc-500" aria-hidden />,
};

/** The title and the line under it, as the sign-up page shows them. */
export const AccessChoiceHeading = () => (
  <>
    <h1 className="text-2xl font-semibold">{ACCESS_CHOICE_TITLE}</h1>
    <p className="text-muted-foreground leading-6">{ACCESS_CHOICE_INTRO}</p>
  </>
);

/** One ticked card per kind of access the provider offers. */
export const AccessChoiceList = ({
  offered,
  selected,
  onToggle,
}: {
  offered: readonly AccessItem[];
  selected: readonly string[];
  onToggle: (id: string) => void;
}) => (
  <div className="flex flex-col gap-3 w-full">
    {offered.map((item) => (
      <CheckboxCard
        key={item.id}
        label={item.title}
        description={item.description}
        icon={ACCESS_ICONS[item.id]}
        descriptionClassName="whitespace-normal overflow-visible text-clip"
        checked={selected.includes(item.id)}
        onCheckedChange={() => onToggle(item.id)}
      />
    ))}
  </div>
);
